"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CoreFeaturesProviderForSSR = exports.CoreFeaturesProvider = exports.fetchFeatureFlagAndExperimentAssignments = exports.CoreFeaturesContext = void 0;
const core_features_sdk_1 = require("@glossgenius/core-features-sdk");
const react_1 = __importDefault(require("react"));
const types_1 = require("./types");
exports.CoreFeaturesContext = react_1.default.createContext({
    isLoading: true,
    getFeatureFlagAssignment: () => undefined,
    getExperimentAssignment: () => undefined,
});
function getFeatureFlagCriteria(flagsArray, businessToken, userGuid, targetedRunTime, userToken, businessTargetingProperties) {
    return flagsArray.map((flag) => {
        let subjectTypeTargetingPropertyConfig;
        let subjectKey;
        const subjectTypeOverride = (0, types_1.isFeatureFlagWithSubjectType)(flag)
            ? flag.subjectTypeOverride
            : undefined;
        const featureFlagKey = (0, types_1.isFeatureFlagWithSubjectType)(flag) ? flag.key : flag;
        if (subjectTypeOverride === core_features_sdk_1.SubjectType.USER_GUID) {
            subjectTypeTargetingPropertyConfig = {
                user: {
                    legacyGuid: userGuid,
                },
            };
            subjectKey = userGuid;
        }
        else if (subjectTypeOverride === core_features_sdk_1.SubjectType.USER_TOKEN) {
            subjectTypeTargetingPropertyConfig = {
                user: {
                    token: userToken,
                },
            };
            subjectKey = userToken;
        }
        else {
            const extraProperties = businessTargetingProperties || {};
            subjectTypeTargetingPropertyConfig = {
                business: {
                    token: businessToken,
                    ...extraProperties,
                },
            };
            subjectKey = businessToken;
        }
        return {
            featureKey: featureFlagKey,
            subjectKey: subjectKey,
            subjectType: subjectTypeOverride || core_features_sdk_1.SubjectType.BUSINESS_TOKEN,
            targetingProperties: {
                ...subjectTypeTargetingPropertyConfig,
                runtime: targetedRunTime,
            },
        };
    });
}
function getExperimentsCriteria(experimentsArray, businessGuid, businessToken, userGuid, userToken, targetedRunTime, businessTargetingProperties) {
    return experimentsArray.map((exp) => {
        let subjectTypeTargetingPropertyConfig;
        let subjectKey;
        if (exp.subjectTypeOverride === core_features_sdk_1.SubjectType.USER_TOKEN) {
            subjectTypeTargetingPropertyConfig = {
                user: {
                    token: userToken,
                },
            };
            subjectKey = userToken;
        }
        else if (exp.subjectTypeOverride === core_features_sdk_1.SubjectType.USER_GUID) {
            subjectTypeTargetingPropertyConfig = {
                user: {
                    legacyGuid: userGuid,
                },
            };
            subjectKey = userGuid;
        }
        else if (exp.subjectTypeOverride === core_features_sdk_1.SubjectType.BUSINESS_GUID) {
            const extraProperties = businessTargetingProperties || {};
            subjectTypeTargetingPropertyConfig = {
                business: {
                    ...extraProperties,
                    legacyGuid: businessGuid,
                },
            };
            subjectKey = businessGuid;
        }
        else {
            const extraProperties = businessTargetingProperties || {};
            subjectTypeTargetingPropertyConfig = {
                business: {
                    ...extraProperties,
                    token: businessToken,
                },
            };
            subjectKey = businessToken;
        }
        return {
            featureKey: exp.key,
            subjectKey: subjectKey,
            subjectType: exp.subjectTypeOverride || core_features_sdk_1.SubjectType.BUSINESS_TOKEN,
            targetingProperties: {
                ...subjectTypeTargetingPropertyConfig,
                runtime: targetedRunTime,
            },
        };
    });
}
function getAssignmentValue(assignment) {
    let result = assignment.stringValue ||
        assignment.numericValue ||
        assignment.booleanValue;
    if (result === undefined) {
        result = assignment.defaultValue;
    }
    return result;
}
function fetchAssignments(postSender, criteria, hydrateTargetingProperties) {
    /** hydrateTargetingProperties is used for non-SSR clients that want the BFF
     * to hydrate targeting properties for them.
     */
    return postSender(`/v1/features/client/evaluate${hydrateTargetingProperties ? '?hydrateTargetingProperties=true' : ''}`, {
        criteria,
    });
}
// eslint-disable-next-line sonarjs/cognitive-complexity
async function fetchFeatureFlagAndExperimentAssignments({ businessGuid, userGuid, businessToken, experiments, featureFlags, postSender, targetedRuntime, businessTargetingProperties, hydrateTargetingProperties = false, userToken, }) {
    const featureFlagAssignments = {};
    const experimentStateMap = {};
    // Convert the feature flags and experiments to arrays so that we can
    // iterate over them
    const flagsArray = Object.values(featureFlags);
    const flagsKeysArray = flagsArray.map((flag) => (0, types_1.isFeatureFlagWithSubjectType)(flag) ? flag.key : flag);
    const experimentsArray = Object.values(experiments);
    try {
        const ffCriteria = getFeatureFlagCriteria(flagsArray, businessToken, userGuid, targetedRuntime, userToken, businessTargetingProperties);
        const experimentCriteria = getExperimentsCriteria(experimentsArray, businessGuid, businessToken, userGuid, userToken, targetedRuntime, businessTargetingProperties);
        const response = await fetchAssignments(postSender, [...ffCriteria, ...experimentCriteria], hydrateTargetingProperties);
        const assignments = response.data.assignments;
        for (const assignment of assignments) {
            const assignmentValue = getAssignmentValue(assignment);
            if (flagsKeysArray.includes(assignment.featureKey)) {
                featureFlagAssignments[assignment.featureKey] = assignmentValue;
            }
            else {
                const experiment = experimentsArray.find((exp) => exp.key === assignment.featureKey);
                if (!experiment) {
                    console.error(`Received assignment for unknown experiment: ${assignment.featureKey}`);
                    continue;
                }
                experimentStateMap[experiment.key] = {
                    assignmentVariantName: assignment.assignmentKey,
                    assignmentValue: assignmentValue || 'control',
                    subjectType: assignment.subjectType || core_features_sdk_1.SubjectType.BUSINESS_TOKEN,
                    subjectKey: assignment.subjectKey,
                    shouldReportAssignment: assignmentValue !== null,
                    isReported: false,
                };
            }
        }
    }
    catch (error) {
        console.error('Failed to fetch feature flag assignments', error);
        flagsArray.forEach((flag) => {
            const flagKey = (0, types_1.isFeatureFlagWithSubjectType)(flag) ? flag.key : flag;
            featureFlagAssignments[flagKey] = false;
        });
        experimentsArray.forEach((exp) => {
            let subjectKey;
            switch (exp.subjectTypeOverride) {
                case core_features_sdk_1.SubjectType.USER_TOKEN:
                    subjectKey = userToken;
                    break;
                case core_features_sdk_1.SubjectType.USER_GUID:
                    subjectKey = userGuid;
                    break;
                case core_features_sdk_1.SubjectType.BUSINESS_GUID:
                    subjectKey = businessGuid;
                    break;
                default:
                case core_features_sdk_1.SubjectType.BUSINESS_TOKEN:
                    subjectKey = businessToken;
                    break;
            }
            experimentStateMap[exp.key] = {
                assignmentVariantName: 'control',
                assignmentValue: 'control',
                subjectType: exp.subjectTypeOverride || core_features_sdk_1.SubjectType.BUSINESS_TOKEN,
                subjectKey: subjectKey,
                /**
                 * Need to report so that users who were previously assigned
                 * something different from control will be invalidated in data
                 */
                shouldReportAssignment: true,
                isReported: false,
            };
        });
    }
    return { featureFlagAssignments, experimentStateMap };
}
exports.fetchFeatureFlagAndExperimentAssignments = fetchFeatureFlagAndExperimentAssignments;
function reportAssignment(experimentKey, experimentState, assignmentReporter) {
    if (experimentState.shouldReportAssignment && !experimentState.isReported) {
        const assignment = {
            experimentKey,
            subjectType: experimentState.subjectType,
            subjectTypeReportString: (0, core_features_sdk_1.toSubjectTypeReportString)(experimentState.subjectType),
            subjectKey: experimentState.subjectKey,
            assignmentVariantName: experimentState.assignmentVariantName || '',
            assignmentValue: experimentState.assignmentValue,
        };
        try {
            assignmentReporter(assignment);
        }
        catch (error) {
            console.error('Failed to report assignment. This is very bad and should be investigated immediately. The experiment could be invalidated.', `Experiment key: ${experimentKey}`, error);
        }
        experimentState.isReported = true;
    }
}
function CoreFeaturesProvider({ children, businessToken, businessGuid, userGuid, featureFlags, experiments, postSender, assignmentReporter, targetedRuntime, userToken, }) {
    const [isLoading, setIsLoading] = react_1.default.useState(true);
    // Using useRef instead of useState so that only the loading state will
    // cause rerender
    const featureFlagAssignments = react_1.default.useRef({});
    const experimentStateMap = react_1.default.useRef({});
    react_1.default.useEffect(() => {
        setIsLoading(true);
        fetchFeatureFlagAndExperimentAssignments({
            businessGuid,
            userGuid,
            businessToken,
            experiments,
            featureFlags,
            postSender,
            targetedRuntime,
            hydrateTargetingProperties: true,
            userToken,
        })
            .then(({ featureFlagAssignments: newFeatureFlagAssignments, experimentStateMap: newExperimentStateMap, }) => {
            featureFlagAssignments.current = newFeatureFlagAssignments;
            experimentStateMap.current = newExperimentStateMap;
        })
            .finally(() => setIsLoading(false));
    }, [
        businessGuid,
        userGuid,
        businessToken,
        experiments,
        featureFlags,
        postSender,
        targetedRuntime,
        userToken,
    ]);
    const getFeatureFlagAssignment = (flagKey) => {
        if (featureFlagAssignments.current[flagKey] === undefined && !isLoading) {
            console.error(`Missing data for feature flag: ${flagKey}. Make sure it is defined in the feature flags config.`);
        }
        return featureFlagAssignments.current[flagKey];
    };
    const getExperimentAssignment = (experimentKey) => {
        const experimentState = experimentStateMap.current[experimentKey];
        if (!experimentState) {
            if (!isLoading) {
                console.error(`Missing data for experiment: ${experimentKey}. Make sure it is defined in the experiments config.`);
            }
            return undefined;
        }
        reportAssignment(experimentKey, experimentState, assignmentReporter);
        return experimentState.assignmentValue;
    };
    return (react_1.default.createElement(exports.CoreFeaturesContext.Provider, { value: {
            isLoading,
            getFeatureFlagAssignment,
            getExperimentAssignment,
        } }, children));
}
exports.CoreFeaturesProvider = CoreFeaturesProvider;
/**
 * Use this provider if you are server-side rendering the app.
 * Use fetchFeatureFlagAndExperimentAssignments to fetch backend
 * data during server-side rendering, and pass the object it returns
 * to this provider, as coreFeaturesBackendData.
 */
function CoreFeaturesProviderForSSR({ children, coreFeaturesBackendData, assignmentReporter, }) {
    const { featureFlagAssignments, experimentStateMap } = coreFeaturesBackendData;
    const getFeatureFlagAssignment = (flagKey) => {
        if (featureFlagAssignments[flagKey] === undefined) {
            console.error(`Missing data for feature flag: ${flagKey}. Make sure it is defined in the feature flags config.`);
        }
        return featureFlagAssignments[flagKey];
    };
    const getExperimentAssignment = (experimentKey) => {
        const experimentState = experimentStateMap[experimentKey];
        if (!experimentState) {
            console.error(`Missing data for experiment: ${experimentKey}. Make sure it is defined in the experiments config.`);
            return undefined;
        }
        reportAssignment(experimentKey, experimentState, assignmentReporter);
        return experimentState.assignmentValue;
    };
    return (react_1.default.createElement(exports.CoreFeaturesContext.Provider, { value: {
            isLoading: false,
            getFeatureFlagAssignment,
            getExperimentAssignment,
        } }, children));
}
exports.CoreFeaturesProviderForSSR = CoreFeaturesProviderForSSR;
